import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Container, Card, CardContent, CardHeader } from "@kmf/ui";
import jakim from "@/assets/jakim.png";
import muis from "@/assets/muis.png";
import bpjph from "@/assets/bpjph-emblem.png";
import thailandHalal from "@/assets/thailand-halal.png";
import ifanca from "@/assets/ifanca.png";
import ara from "@/assets/ara.png";
import hccc from "@/assets/hccc.png";
import idcp from "@/assets/idcp.png";
import kfri from "@/assets/kfri.png";
import at from "@/assets/at.png";
import kotra from "@/assets/kotra.png";
import kHalal from "@/assets/k-halal.png";
import hdc from "@/assets/hdc.png";
import bsq from "@/assets/bsq.png";
import biotech from "@/assets/kogeneBiotech.png";
import smiic from "@/assets/smiic.png";
import ktr from "@/assets/ktr.png";
import qatarMoph from "@/assets/qatar-moph.svg";
import saudiShc from "@/assets/saudi-shc.svg";

const recognizingBodies = [
  { name: "말레이시아 JAKIM", logo: jakim },
  { name: "인도네시아 BPJPH", logo: bpjph, href: "/recognition/bpjph", standard: "SJPH" },
  { name: "싱가포르 MUIS", logo: muis },
  { name: "태국 CICOT", logo: thailandHalal },
  { name: "카타르 식약처", logo: qatarMoph },
  { name: "사우디 SHC (예정)", logo: saudiShc },
];

const certificationBodies = [
  { name: "미국 IFANCA", logo: ifanca },
  { name: "중국 ARA", logo: ara },
  { name: "칠레 H.C.C.C.", logo: hccc },
  { name: "필리핀 IDCP", logo: idcp },
];

// 주요 협력기관
const partnerOrgs = [
  { name: "KTR", logo: ktr },
  { name: "한국식품연구원 (KFRI)", logo: kfri },
  { name: "한국농수산식품유통공사 (aT)", logo: at },
  { name: "KOTRA", logo: kotra },
  { name: "한국할랄산업연구원 (K-Halal)", logo: kHalal },
  { name: "할랄개발공사 (HDC)", logo: hdc },
  { name: "비스퀘어 (BSQ)", logo: bsq },
  { name: "한국바이오협회", logo: biotech },
  { name: "SMIIC", logo: smiic },
];

export function AboutPage() {
  useEffect(() => {
    document.title = "소개 - KMF 할랄위원회";
  }, []);

  return (
    <div className="public-page py-12 md:py-16">
      <Container>
        {/* Page Header */}
        <div className="public-page-header">
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            KMF 할랄위원회 소개
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            한국 최초의 국제 상호 인증 할랄 인증기관
          </p>
        </div>

        {/* About Content */}
        <div className="content-column mx-auto max-w-4xl space-y-8">
          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">KMF 할랄위원회</h2>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <p>
                KMF 할랄위원회는 (재)한국이슬람교(KMF) 산하 기관으로,
                한국에서 최초로 국제적으로 인정받는 할랄 인증 서비스를 제공하고 있습니다.
              </p>
              <p>
                말레이시아 JAKIM, 인도네시아 BPJPH, 싱가포르 MUIS, 태국 CICOT, 카타르 식약처의 인정을 받고 있으며,
                미국 IFANCA, 중국 ARA, 칠레 H.C.C.C., 필리핀 IDCP와 상호인정 관계를 맺고 있습니다.
              </p>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">인정기관</h2>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
                {recognizingBodies.map((b) => (
                  <div key={b.name} className="flex flex-col items-center gap-2">
                    <div className="public-logo-frame flex h-20 w-full items-center justify-center rounded-lg border bg-white p-3">
                      <img src={b.logo} alt={b.name} className="max-h-14 w-auto object-contain" />
                    </div>
                    <span className="text-center text-xs text-muted-foreground">
                      {b.name}
                    </span>
                    {b.href && <Link to={b.href} className="logo-standard">인증 기준: {b.standard} <span aria-hidden="true">→</span></Link>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">상호인정 해외인증기관</h2>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
                {certificationBodies.map((b) => (
                  <div key={b.name} className="flex flex-col items-center gap-2">
                    <div className="public-logo-frame flex h-20 w-full items-center justify-center rounded-lg border bg-white p-3">
                      <img
                        src={b.logo}
                        alt={b.name}
                        className="max-h-14 w-auto object-contain"
                      />
                    </div>
                    <span className="text-center text-xs text-muted-foreground">
                      {b.name}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">주요 협력기관</h2>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
                {partnerOrgs.map((o) => (
                  <div key={o.name} className="flex flex-col items-center gap-2">
                    <div className="public-logo-frame flex h-20 w-full items-center justify-center rounded-lg border bg-white p-3">
                      <img
                        src={o.logo}
                        alt={o.name}
                        className="max-h-14 w-auto object-contain"
                      />
                    </div>
                    <span className="text-center text-xs text-muted-foreground">
                      {o.name}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">미션과 비전</h2>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="public-item rounded-lg bg-primary/5 p-6">
                  <h3 className="mb-3 text-lg font-semibold text-primary">미션</h3>
                  <p className="text-muted-foreground">
                    한국 기업의 할랄 시장 진출을 지원하고,
                    무슬림 소비자에게 신뢰할 수 있는 할랄 제품을 제공합니다.
                  </p>
                </div>
                <div className="public-item rounded-lg bg-secondary/5 p-6">
                  <h3 className="mb-3 text-lg font-semibold text-secondary-foreground">비전</h3>
                  <p className="text-muted-foreground">
                    아시아 최고의 할랄 인증기관으로서
                    글로벌 할랄 산업 발전에 기여합니다.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">주요 업무</h2>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-4 md:grid-cols-2">
                {[
                  "할랄 인증 심사 및 발급",
                  "할랄 컨설팅 서비스",
                  "할랄 교육 프로그램 운영",
                  "할랄 시장 정보 제공",
                  "국제 할랄 기관 교류 협력",
                  "할랄 인증 제품 모니터링",
                ].map((item, index) => (
                  <li
                    key={index}
                    className="public-item flex items-center gap-3 rounded-lg border p-4"
                  >
                    <div className="step-number flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                      {index + 1}
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="public-panel">
            <CardHeader>
              <h2 className="text-xl font-semibold">연혁</h2>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { year: "2011", event: "KMF 할랄위원회 설립" },
                  { year: "2012", event: "말레이시아 JAKIM 인정" },
                  { year: "2013", event: "싱가포르 MUIS 인정" },
                  { year: "2015", event: "UAE 인증기관과 협력 체계 구축" },
                  { year: "2020", event: "블록체인 기반 할랄 인증 시스템 도입" },
                ].map((item) => (
                  <div
                    key={item.year}
                    className="flex items-start gap-4 border-l-2 border-primary pl-4"
                  >
                    <span className="shrink-0 font-bold text-primary">
                      {item.year}
                    </span>
                    <span className="text-muted-foreground">{item.event}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
    </div>
  );
}
